
    import { Component, Vue, Watch } from 'vue-property-decorator';
    import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
    import ImpfstoffeDetail from '@/components/Impfstoffe/ImpfstoffeDetail.vue';
    import Impfstoff from '@/types/Impfstoffe';
    import { ExportImpfstoffePayload, ImpfstoffeFilter, LoadImpfstoffePayload } from '@/store/impfstoffe';
    import { DataTableHeaders } from '@/types';
    import {SystemEinstellung, SystemEinstellungKey} from '@/types/Einstellung';

    @Component({
        components: {
            ImpfstoffeDetail,
        },
        computed: {
            ...mapState('impfstoffe', ['impfstoffe', 'dialogs', 'loaders']),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapGetters('authentication', ['getSystemEinstellung']),
        },
        methods: {
            ...mapActions('impfstoffe', ['loadImpfstoffe', 'exportImpfstoffe']),
            ...mapMutations('impfstoffe', ['setDialogsDetail']),
        },
    })
    export default class Impfstoffe extends Vue {
        public impfstoff = new Impfstoff();

        public search = '';
        public currentSort: { by: (string | undefined)[]; desc: (boolean | undefined)[] } = { by: [], desc: [] };

        public filter: ImpfstoffeFilter = {
            gueltig: 'j',
        };

        public impfstoffe!: Impfstoff[];
        public loadImpfstoffe!: (payload?: LoadImpfstoffePayload) => void;
        public exportImpfstoffe!: (payload?: ExportImpfstoffePayload) => void;
        public setDialogsDetail!: (visible: boolean) => void;
        public getSystemEinstellung!: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T>;

        public get headers(): DataTableHeaders<Impfstoff> {
            if (this.getSystemEinstellung('muw').isEnabled()) {
                return [
                    {text: '', value: 'actions', sortable: false, width: 50},
                    {text: 'Impfstoff', value: 'impfstoff'},
                    {text: 'Bezeichnung', value: 'bezeichnung'},
                    {text: 'Code', value: 'impfstoff_code'},
                    {text: 'Verkaufspreis', value: 'verkaufspreis'},
                    {text: 'Preis Mitarbeiter', value: 'preis_mitarbeiter'},
                    {text: 'Preis Rektorat', value: 'preis_rektorat'},
                    {text: 'Rabatt Mitarbeiter', value: 'rabattMitarbeiter'},
                    {text: 'Aufschlag Rektorat', value: 'aufschlagRektorat'},
                    {text: 'Steuersatz', value: 'steuersatz'},
                    {text: 'HCW', value: 'hcw'},
                    {text: 'Anmerkung', value: 'anmerkung'},
                    {text: 'Gültig', value: 'gueltig'},
                ];
            } else {
                return [
                    {text: '', value: 'actions', sortable: false, width: 50},
                    {text: 'Impfstoff', value: 'impfstoff'},
                    {text: 'Bezeichnung', value: 'bezeichnung'},
                    {text: 'Code', value: 'impfstoff_code'},
                    {text: 'Verkaufspreis', value: 'verkaufspreis'},
                    {text: 'Steuersatz', value: 'steuersatz'},
                    {text: 'Anmerkung', value: 'anmerkung'},
                    {text: 'Gültig', value: 'gueltig'},
                ];
            }
        }

        public impfstoffeSelect = {
            impfdosen: true,
        };

        @Watch('dialogs.edit')
        public onChangeImpfstoffeDialog(visible: boolean): void {
            if (!visible) {
                this.impfstoff = new Impfstoff();
            }
        }

        public mounted(): void {
            this.onChangeFilter();
        }

        public onChangeFilter(): void {
            this.loadImpfstoffe({ filter: this.filter, ...this.impfstoffeSelect });
        }

        public onClickEdit(impfstoff: Impfstoff): void {
            this.impfstoff = new Impfstoff(impfstoff);
            this.setDialogsDetail(true);
        }

        public onClickNew(): void {
            this.impfstoff = new Impfstoff();
            this.setDialogsDetail(true);
        }

        public onClickExport(): void {
            this.exportImpfstoffe({ filter: this.filter });
        }

        public getSortedImpfstoffe(): Impfstoff[] {
            const [key] = this.currentSort.by;
            const [desc] = this.currentSort.desc;

            if (key === undefined || desc === undefined) {
                return this.impfstoffe;
            }

            type comparator = (a: Impfstoff, b: Impfstoff) => number;

            /* eslint-disable @typescript-eslint/no-explicit-any */
            const by = (desc: boolean): comparator => desc
                ? (a: any, b: any): number => {
                    const v1 = a[key];
                    const v2 = b[key];
                    return v1 === v2 ? 0 : v1 > v2 ? -1 : 1;
                } : (a: any, b: any): number => {
                    const v1 = a[key];
                    const v2 = b[key];
                    return v1 === v2 ? 0 : v1 > v2 ? 1 : -1;
                };

            return this.impfstoffe.sort(by(desc));
        }

        public onClickPrevious(): void {
            const impfstoffe = this.getSortedImpfstoffe();
            if (impfstoffe.length === 0) {
                return;
            }

            const index = impfstoffe.map(({ impfstoff_id }) => impfstoff_id).indexOf(this.impfstoff.impfstoff_id);

            this.impfstoff = {
                ...(index === 0)
                    ? impfstoffe[impfstoffe.length - 1]
                    : impfstoffe[index - 1],
            };
        }

        public onClickNext(): void {
            const impfstoffe = this.getSortedImpfstoffe();
            if (impfstoffe.length === 0) {
                return;
            }

            const index = impfstoffe.map(({ impfstoff_id }) => impfstoff_id).indexOf(this.impfstoff.impfstoff_id);

            this.impfstoff = {
                ...(index === impfstoffe.length - 1)
                    ? impfstoffe[0]
                    : impfstoffe[index + 1],
            };
        }

        public onClickClose(): void {
            this.setDialogsDetail(false);
        }

        public onUpdateSortBy(by: string[]): void {
            this.currentSort.by = by;
        }

        public onUpdateSortDesc(desc: boolean[]): void {
            this.currentSort.desc = desc;
        }
    }
